import { useState, useCallback } from 'react';
import { useRevalidator } from 'react-router';
import emailSyncConfigService from '../services/emailSyncConfigService';
import { authedApiFetch } from '../../../utils/Api';
import { formatDateTime } from '../../../utils/utils';

export const useEmailSyncConfig = (
	tenantLevel,
	tenant,
	user,
	showNotification,
	onConfigChange
) => {
	const [isLoading, setIsLoading] = useState(false);
	const revalidator = useRevalidator();

	// Generic handler for API calls with consistent error handling
	const handleApiCall = useCallback(
		async (apiCall, message) => {
			const { successMessage, errorMessage, errorLog } = message;

			setIsLoading(true);
			try {
				const result = await apiCall();
				revalidator.revalidate();
				if (successMessage) {
					showNotification(successMessage, 'success');
				}
				if (onConfigChange) {
					onConfigChange();
				}
				return { success: true, data: result };
			} catch (error) {
				console.error(errorLog, error.message);
				showNotification(errorMessage, 'error');
				return { success: false, error };
			} finally {
				setIsLoading(false);
			}
		},
		[revalidator, onConfigChange]
	);

	// Microsoft auto sync toggle
	const updateUserMicrosoftAutoSync = useCallback(
		async (newValue) => {
			const apiCall = async () => {
				if (newValue) {
					// Enable: POST to create
					return authedApiFetch({
						endpoint: `/tenants/${tenant.id}/users/${user.id}/email_sync_configurations`,
						method: 'POST',
						payload: {
							email_sync_configuration: {
								provider: 'microsoft',
								enabled: true,
							},
						},
					});
				} else {
					// Disable: need to find the config ID first
					const configs = await authedApiFetch({
						endpoint: `/tenants/${tenant.id}/users/${user.id}/email_sync_configurations`,
						method: 'GET',
					});
					const config = configs.find((c) => c.provider === 'microsoft');
					if (!config) throw new Error('Configuration not found');

					return authedApiFetch({
						endpoint: `/tenants/${tenant.id}/users/${user.id}/email_sync_configurations/${config.id}`,
						method: 'PATCH',
						payload: {
							email_sync_configuration: {
								enabled: false,
								sync_start_at: null,
							},
						},
					});
				}
			};

			const message = {
				successMessage: `Microsoft automatic sync ${newValue ? 'enabled' : 'disabled'}.`,
				errorMessage: `Failed to ${newValue ? 'enable' : 'disable'} Microsoft automatic sync.`,
				errorLog:
					'Failed to save Microsoft auto sync setting due to the error:',
			};

			return handleApiCall(apiCall, message);
		},
		[user.id, tenant.id, handleApiCall]
	);

	// Gmail auto sync toggle
	const updateUserGmailAutoSync = useCallback(
		async (newValue) => {
			const apiCall = async () => {
				if (newValue) {
					// Enable: POST to create
					return authedApiFetch({
						endpoint: `/tenants/${tenant.id}/users/${user.id}/email_sync_configurations`,
						method: 'POST',
						payload: {
							email_sync_configuration: { provider: 'google', enabled: true },
						},
					});
				} else {
					// Disable: need to find the config ID first
					const configs = await authedApiFetch({
						endpoint: `/tenants/${tenant.id}/users/${user.id}/email_sync_configurations`,
						method: 'GET',
					});
					const config = configs.find((c) => c.provider === 'google');
					if (!config) throw new Error('Configuration not found');

					return authedApiFetch({
						endpoint: `/tenants/${tenant.id}/users/${user.id}/email_sync_configurations/${config.id}`,
						method: 'PATCH',
						payload: { email_sync_configuration: { enabled: false } },
					});
				}
			};

			const message = {
				successMessage: `Gmail automatic sync ${newValue ? 'enabled' : 'disabled'}.`,
				errorMessage: `Failed to ${newValue ? 'enable' : 'disable'} Gmail automatic sync.`,
				errorLog: 'Failed to save Gmail auto sync setting due to the error:',
			};

			return handleApiCall(apiCall, message);
		},
		[user.id, tenant.id, handleApiCall]
	);

	// Microsoft auto sync start time
	const updateUserMicrosoftAutoSyncStart = useCallback(
		async (newValue) => {
			const sync_start_at = newValue.toISOString();

			const apiCall = () =>
				authedApiFetch({
					endpoint: `/tenants/${tenant.id}/users/${user.id}/email_sync_configurations`,
					method: 'POST',
					payload: {
						email_sync_configuration: {
							provider: 'microsoft',
							enabled: true,
							sync_start_at,
						},
					},
				});

			const formattedSyncStart = formatDateTime(sync_start_at);

			const message = {
				successMessage: `Microsoft automatic sync will start from ${formattedSyncStart}.`,
				errorMessage: `Failed to update the start time of next Microsoft automatic sync.`,
				errorLog:
					'Failed to update the start time of next Microsoft automatic sync due to the error:',
			};

			return handleApiCall(apiCall, message);
		},
		[user.id, tenant.id, handleApiCall]
	);

	// Gmail auto sync start time
	const updateUserGmailAutoSyncStart = useCallback(
		async (newValue) => {
			const sync_start_at = newValue.toISOString();

			const apiCall = () =>
				authedApiFetch({
					endpoint: `/tenants/${tenant.id}/users/${user.id}/email_sync_configurations`,
					method: 'POST',
					payload: {
						email_sync_configuration: {
							provider: 'google',
							enabled: true,
							sync_start_at,
						},
					},
				});

			const formattedSyncStart = formatDateTime(sync_start_at);

			const message = {
				successMessage: `Gmail automatic sync will start from ${formattedSyncStart}.`,
				errorMessage: `Failed to update the start time of next Gmail automatic sync.`,
				errorLog:
					'Failed to update the start time of next Gmail automatic sync due to the error:',
			};

			return handleApiCall(apiCall, message);
		},
		[user.id, tenant.id, handleApiCall]
	);
	// "Include Email Body in CRM Sync" toggle: updates tenant.email_body_sync or user.email_body_sync field
	const updateEmailBodySync = useCallback(
		async (newValue) => {
			const keyVal = { email_body_sync: newValue };
			const apiCall = () =>
				tenantLevel
					? emailSyncConfigService.updateTenant(tenant.id, keyVal)
					: authedApiFetch({
							endpoint: `/tenants/${tenant.id}/users/${user.id}`,
							method: 'PATCH',
							payload: { user: keyVal },
						});

			const message = {
				successMessage: `The "Include Email Body in CRM Sync" setting is toggled ${newValue ? 'on' : 'off'}.`,
				errorMessage:
					'The "Include Email Body toggle CRM Sync" setting failed to update.',
				errorLog:
					'Failed to update the email_body_sync field due to the error:',
			};

			return handleApiCall(apiCall, message);
		},
		[tenantLevel, tenant.id, user.id, handleApiCall]
	);

	// Rule type toggle (inclusion/exclusion)
	const updateTenantRuleType = useCallback(
		async (newValue) => {
			const apiCall = () =>
				emailSyncConfigService.updateTenant(tenant.id, {
					use_inclusion_email_rules: newValue,
				});

			const message = {
				successMessage: `Email rule type changed to ${newValue ? 'inclusion' : 'exclusion'}.`,
				errorMessage: 'Failed to change email rule type.',
				errorLog:
					'Failed to update tenant.use_inclusion_email_rules due to the error:',
			};

			return handleApiCall(apiCall, message);
		},
		[tenant.id, handleApiCall]
	);

	// Fetch existing email sync configurations
	const fetchEmailSyncConfigurations = useCallback(async () => {
		const apiCall = () =>
			authedApiFetch({
				endpoint: `/tenants/${tenant.id}/users/${user.id}/email_sync_configurations`,
				method: 'GET',
			});

		const message = {
			successMessage: '',
			errorMessage: 'Failed to load email sync configurations.',
			errorLog: 'Failed to fetch email sync configurations due to the error:',
		};

		return handleApiCall(apiCall, message);
	}, [user.id, tenant.id, handleApiCall]);

	// Ensure Microsoft config exists (creates if needed)
	const ensureMicrosoftConfigExists = useCallback(async () => {
		try {
			const configs = await authedApiFetch({
				endpoint: `/tenants/${tenant.id}/users/${user.id}/email_sync_configurations`,
				method: 'GET',
			});
			const microsoftConfig = configs.find((c) => c.provider === 'microsoft');
			if (microsoftConfig) {
				return { success: true, configId: microsoftConfig.id };
			}
			// Create config if it doesn't exist
			const newConfig = await authedApiFetch({
				endpoint: `/tenants/${tenant.id}/users/${user.id}/email_sync_configurations`,
				method: 'POST',
				payload: {
					email_sync_configuration: { provider: 'microsoft', enabled: false },
				},
			});
			return { success: true, configId: newConfig.id };
		} catch (error) {
			console.error('Failed to ensure Microsoft config exists:', error.message);
			return { success: false, error };
		}
	}, [user.id, tenant.id]);

	// Ensure Gmail config exists (creates if needed)
	const ensureGmailConfigExists = useCallback(async () => {
		try {
			const configs = await authedApiFetch({
				endpoint: `/tenants/${tenant.id}/users/${user.id}/email_sync_configurations`,
				method: 'GET',
			});
			const gmailConfig = configs.find((c) => c.provider === 'google');
			if (gmailConfig) {
				return { success: true, configId: gmailConfig.id };
			}
			// Create config if it doesn't exist
			const newConfig = await authedApiFetch({
				endpoint: `/tenants/${tenant.id}/users/${user.id}/email_sync_configurations`,
				method: 'POST',
				payload: {
					email_sync_configuration: { provider: 'google', enabled: false },
				},
			});
			return { success: true, configId: newConfig.id };
		} catch (error) {
			console.error('Failed to ensure Gmail config exists:', error.message);
			return { success: false, error };
		}
	}, [user.id, tenant.id]);

	// Fetch Microsoft folders for a configuration
	const fetchMicrosoftFolders = useCallback(
		async (configId) => {
			try {
				const folders = await authedApiFetch({
					endpoint: `/tenants/${tenant.id}/users/${user.id}/email_sync_configurations/${configId}/folders`,
					method: 'GET',
				});
				return { success: true, data: folders };
			} catch (error) {
				console.error('Failed to fetch Microsoft folders:', error.message);
				return { success: false, error };
			}
		},
		[user.id, tenant.id]
	);

	// Fetch Gmail labels for a configuration
	const fetchGmailLabels = useCallback(
		async (configId) => {
			try {
				const labels = await authedApiFetch({
					endpoint: `/tenants/${tenant.id}/users/${user.id}/email_sync_configurations/${configId}/labels`,
					method: 'GET',
				});
				return { success: true, data: labels };
			} catch (error) {
				console.error('Failed to fetch Gmail labels:', error.message);
				return { success: false, error };
			}
		},
		[user.id, tenant.id]
	);

	return {
		updateUserMicrosoftAutoSync,
		updateUserGmailAutoSync,
		updateUserMicrosoftAutoSyncStart,
		updateUserGmailAutoSyncStart,
		updateEmailBodySync,
		updateTenantRuleType,
		fetchEmailSyncConfigurations,
		ensureMicrosoftConfigExists,
		ensureGmailConfigExists,
		fetchMicrosoftFolders,
		fetchGmailLabels,
		isLoading,
	};
};
